import type { Locale } from './config'

type ProductCard = {
  title: string
  description: string
}

type FeatureCard = {
  title: string
  description: string
}

type HelpTopic = {
  id: string
  title: string
  articlesLabel: string
}

type HelpFaq = {
  q: string
  a: string
}

type UiCopy = {
  header: {
    menuAriaLabel: string
    userFallback: string
    subscription: string
    pricing: string
    helpSupport: string
    moreNavigation: string
  }
  footer: {
    description: string
    explore: string
    create: string
    support: string
    company: string
    stayConnected: string
    emailPlaceholder: string
    subscribe: string
    emailSupport: string
    phoneSupport: string
    globalReach: string
    globalReachValue: string
    privacy: string
    terms: string
    cookies: string
    accessibility: string
    rightsReserved: string
    exploreLinks: {
      discover: string
      events: string
      community: string
    }
    createLinks: {
      listing: string
      event: string
    }
    supportLinks: {
      helpCenter: string
      safety: string
      guidelines: string
    }
    companyLinks: {
      about: string
      careers: string
      blog: string
    }
  }
  home: {
    badge: string
    heroTitleStart: string
    heroTitleHighlight: string
    heroSubtitleStart: string
    heroSubtitleEmphasis: string
    heroSubtitleEnd: string
    primaryCta: string
    secondaryCta: string
    socialProof: string
    productsEyebrow: string
    productsTitleStart: string
    productsTitleHighlight: string
    productsSubtitle: string
    products: ProductCard[]
    explore: string
    whyEyebrow: string
    whyTitleStart: string
    whyTitleHighlight: string
    features: FeatureCard[]
    finalTitleStart: string
    finalTitleHighlight: string
    finalSubtitle: string
    finalPrimaryCta: string
    finalSecondaryCta: string
    stats: Array<{ value: string; label: string }>
    footerDescription: string
  }
  login: {
    backToHome: string
    joinTitle: string
    welcomeTitle: string
    joinSubtitle: string
    welcomeSubtitle: string
    continueWithGoogle: string
    continueWithEmail: string
    alreadyHaveAccount: string
    newToMengir: string
    signIn: string
    createAccount: string
    secure: string
    instantAccess: string
    termsIntro: string
    termsOfService: string
    privacyPolicy: string
    loading: string
    errors: {
      sessionExpired: string
      configuration: string
      accessDenied: string
      verification: string
      oauthSignin: string
      oauthCallback: string
      oauthCreateAccount: string
      callback: string
      oauthAccountNotLinked: string
      sessionRequired: string
      default: string
    }
  }
  community: {
    pageTitle: string
    pageSubtitle: string
    newPost: string
    searchPlaceholder: string
    allPostTypes: string
    emptyState: string
    fallbackTitle: string
    linkedListing: string
    linkedEvent: string
    linkedUserPrefix: string
    postTypes: Record<string, string>
  }
  requests: {
    pageTitle: string
    pageSubtitle: string
    newRequest: string
    searchPlaceholder: string
    allStatuses: string
    emptyState: string
    fallbackTitle: string
    statuses: Record<string, string>
  }
  help: {
    pageTitle: string
    pageSubtitle: string
    searchPlaceholder: string
    quickFeedback: string
    quickFeedbackDescription: string
    getSupport: string
    getSupportDescription: string
    tabs: {
      help: string
      newTicket: string
      myTickets: string
    }
    popularTopics: string
    faqTitle: string
    topics: HelpTopic[]
    faqs: HelpFaq[]
  }
  pricing: {
    pageTitle: string
    pageSubtitle: string
    checkoutCancelledTitle: string
    checkoutCancelledBody: string
    manage: string
    activeSubscription: string
    freePlan: string
    freePlanDescription: string
    forever: string
    currentPlan: string
    freeFeatures: string[]
    paidFeatures: string[]
    popular: string
    getStarted: string
    needHelpChoosing: string
    allPlansInclude: string
    viewMySubscription: string
    errors: {
      checkoutSession: string
      subscriptionStart: string
      billingPortal: string
      upgradePortal: string
    }
    interval: {
      singlePrefix: string
      multiPrefix: string
      pluralSuffix: string
    }
    aiGenerationsPerMonth: string
  }
  messages: {
    loginRequired: string
    pageTitle: string
    refreshConversations: string
    searchPlaceholder: string
    unreadFilter: string
    emptyConversationsTitle: string
    emptyConversationsBody: string
    noMessagesYet: string
    conversationFallback: string
    generalConversation: string
    moreOptions: string
    view: string
    writeMessage: string
    sendMessage: string
    pressEnterHint: string
    emptyThreadTitle: string
    emptyThreadBody: string
    reportUser: string
    blockUser: string
    loadConversationsError: string
    loadMessagesError: string
    sendMessageError: string
    contextLabels: {
      listing: string
      event: string
      conversation: string
    }
    relativeTime: {
      justNow: string
      minutesAgoSuffix: string
      hoursAgoSuffix: string
      daysAgoSuffix: string
    }
  }
}

export const uiCopy: Record<Locale, UiCopy> = {
  en: {
    header: {
      menuAriaLabel: 'menu',
      userFallback: 'User',
      subscription: 'Subscription',
      pricing: 'Pricing',
      helpSupport: 'Help & Support',
      moreNavigation: 'more navigation',
    },
    footer: {
      description: 'Connecting Ethiopian & Eritrean communities worldwide.',
      explore: 'Explore',
      create: 'Create',
      support: 'Support',
      company: 'Company',
      stayConnected: 'Stay Connected',
      emailPlaceholder: 'Enter your email',
      subscribe: 'Subscribe',
      emailSupport: 'Email Support',
      phoneSupport: 'Phone Support',
      globalReach: 'Global Reach',
      globalReachValue: 'Serving 100+ cities worldwide',
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
      accessibility: 'Accessibility',
      rightsReserved: 'All rights reserved.',
      exploreLinks: {
        discover: 'Discover',
        events: 'Events',
        community: 'Community',
      },
      createLinks: {
        listing: 'Listing',
        event: 'Event',
      },
      supportLinks: {
        helpCenter: 'Help Center',
        safety: 'Safety',
        guidelines: 'Guidelines',
      },
      companyLinks: {
        about: 'About',
        careers: 'Careers',
        blog: 'Blog',
      },
    },
    home: {
      badge: 'Ethiopia. Eritrea. One People. Worldwide.',
      heroTitleStart: 'Connect with your community',
      heroTitleHighlight: 'worldwide',
      heroSubtitleStart: 'Find businesses, housing, jobs, and build meaningful connections across',
      heroSubtitleEmphasis: '200+ cities',
      heroSubtitleEnd: 'globally.',
      primaryCta: 'Start now',
      secondaryCta: 'Explore Products',
      socialProof: 'Trusted by thousands worldwide',
      productsEyebrow: 'Products',
      productsTitleStart: 'A fully integrated suite of',
      productsTitleHighlight: 'community services',
      productsSubtitle: 'Connect your Ethiopian & Eritrean communities worldwide.',
      products: [
        {
          title: 'Browse Listings',
          description: 'Discover businesses, services, and products from Ethiopian & Eritrean communities worldwide.',
        },
        {
          title: 'Properties & Housing',
          description: 'Find your perfect home with listings for rentals, sales, and roommates in Ethiopian communities.',
        },
        {
          title: 'Marketplace',
          description: 'Buy and sell items, crafts, and goods within the Ethiopian & Eritrean community marketplace.',
        },
        {
          title: 'Jobs & Opportunities',
          description: 'Find work opportunities and career advancement within the Ethiopian & Eritrean business network.',
        },
      ],
      explore: 'Explore',
      whyEyebrow: 'Why Mengir',
      whyTitleStart: 'Built for',
      whyTitleHighlight: 'your community',
      features: [
        {
          title: 'Cultural Focus',
          description: 'Purpose-built for Ethiopian & Eritrean communities with cultural understanding at its core.',
        },
        {
          title: 'Global Network',
          description: 'Connect with community members across 200+ cities worldwide.',
        },
        {
          title: 'Trusted Platform',
          description: 'Verified businesses and trusted community members you can rely on.',
        },
        {
          title: 'Growing Community',
          description: 'Join thousands of members actively engaging and supporting each other.',
        },
      ],
      finalTitleStart: 'Ready to',
      finalTitleHighlight: 'get started?',
      finalSubtitle: 'Join thousands of your people connecting and growing worldwide.',
      finalPrimaryCta: 'Get started now',
      finalSecondaryCta: 'Browse Listings',
      stats: [
        { value: '200+', label: 'Cities' },
        { value: '2K+', label: 'Members' },
        { value: '500+', label: 'Businesses' },
      ],
      footerDescription: 'Connecting Ethiopian & Eritrean communities worldwide',
    },
    login: {
      backToHome: 'Back to Home',
      joinTitle: 'Join Mengir',
      welcomeTitle: 'Welcome to Mengir',
      joinSubtitle: 'Create your account in minutes.',
      welcomeSubtitle: 'Connect with the Ethiopian & Eritrean Community',
      continueWithGoogle: 'Continue with Google',
      continueWithEmail: 'Continue with Email',
      alreadyHaveAccount: 'Already have an account?',
      newToMengir: 'New to Mengir?',
      signIn: 'Sign in',
      createAccount: 'Create account',
      secure: 'Secure',
      instantAccess: 'Instant Access',
      termsIntro: 'By continuing, you agree to our',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      loading: 'Loading...',
      errors: {
        sessionExpired: 'Your session expired. Please sign in again.',
        configuration: 'Server configuration problem.',
        accessDenied: 'Access denied. You may not have permission.',
        verification: 'Verification token expired or already used.',
        oauthSignin: 'Error constructing authorization URL.',
        oauthCallback: 'Authentication failed. Please try again.',
        oauthCreateAccount: 'Could not create account.',
        callback: 'Authentication failed. Please try again.',
        oauthAccountNotLinked: 'Email already linked to a different account.',
        sessionRequired: 'Sign in required to access this content.',
        default: 'An authentication error occurred. Please try again.',
      },
    },
    community: {
      pageTitle: 'Community Feed',
      pageSubtitle: 'Share announcements, ask questions, and spotlight community wins.',
      newPost: 'New Post',
      searchPlaceholder: 'Search community posts',
      allPostTypes: 'All post types',
      emptyState: 'No community posts yet. Be the first to share!',
      fallbackTitle: 'Community Update',
      linkedListing: 'Listing',
      linkedEvent: 'Event',
      linkedUserPrefix: 'User',
      postTypes: {
        announcement: 'Announcement',
        question: 'Question',
        recommendation: 'Recommendation',
        story: 'Success Story',
        discussion: 'Discussion',
      },
    },
    requests: {
      pageTitle: 'Requests & Wanted',
      pageSubtitle: 'Share what you need and connect with helpers in the community.',
      newRequest: 'New Request',
      searchPlaceholder: 'Search requests',
      allStatuses: 'All statuses',
      emptyState: 'No requests yet. Start the conversation with a new request!',
      fallbackTitle: 'Community Request',
      statuses: {
        open: 'Open',
        matched: 'Matched',
        closed: 'Closed',
        archived: 'Archived',
      },
    },
    help: {
      pageTitle: 'How can we help?',
      pageSubtitle: 'Search for answers, get support, or share your feedback',
      searchPlaceholder: 'Search help articles...',
      quickFeedback: 'Quick Feedback',
      quickFeedbackDescription: 'Rate your experience and share suggestions',
      getSupport: 'Get Support',
      getSupportDescription: 'Report an issue or ask for help',
      tabs: {
        help: 'Help',
        newTicket: 'New Ticket',
        myTickets: 'My Tickets',
      },
      popularTopics: 'Popular Topics',
      faqTitle: 'Frequently Asked Questions',
      topics: [
        { id: 'listings', title: 'Creating & Managing Listings', articlesLabel: '5 articles' },
        { id: 'account', title: 'Account Settings', articlesLabel: '3 articles' },
        { id: 'payments', title: 'Payments & Billing', articlesLabel: '4 articles' },
        { id: 'safety', title: 'Safety & Trust', articlesLabel: '6 articles' },
      ],
      faqs: [
        { q: 'How do I create a listing?', a: 'Click the "Create Listing" button and follow the step-by-step wizard.' },
        { q: 'How do I contact a seller?', a: 'Use the "Message" button on any listing to start a conversation.' },
        { q: 'How do I upgrade my subscription?', a: 'Go to Account > Subscription to view and manage your plan.' },
        { q: 'How do I report a problem?', a: 'Use the "Get Support" option below to create a support ticket.' },
      ],
    },
    pricing: {
      pageTitle: 'Choose Your Plan',
      pageSubtitle: 'Unlock AI-powered content creation and premium features for your community',
      checkoutCancelledTitle: 'Checkout cancelled',
      checkoutCancelledBody: 'No worries! You can subscribe anytime. Choose a plan below to get started.',
      manage: 'Manage',
      activeSubscription: 'You have an active subscription',
      freePlan: 'Free',
      freePlanDescription: 'Get started with basic features',
      forever: 'forever',
      currentPlan: 'Current Plan',
      freeFeatures: [
        '5 AI-powered listings/events',
        'Basic community access',
        'Standard search visibility',
        'Email support',
      ],
      paidFeatures: [
        'Priority AI content generation',
        'Enhanced search visibility',
        'Premium badge on listings',
        'Advanced analytics',
        'Priority customer support',
        'Early access to new features',
      ],
      popular: 'Popular',
      getStarted: 'Get Started',
      needHelpChoosing: 'Need help choosing?',
      allPlansInclude: 'All plans include unlimited browsing and networking',
      viewMySubscription: 'View My Subscription',
      errors: {
        checkoutSession: 'Failed to create checkout session',
        subscriptionStart: 'Failed to start subscription. Please try again.',
        billingPortal: 'Failed to open billing portal',
        upgradePortal: 'Failed to open upgrade portal. Please try again.',
      },
      interval: {
        singlePrefix: '/ ',
        multiPrefix: '/ ',
        pluralSuffix: 's',
      },
      aiGenerationsPerMonth: 'AI generations per month',
    },
    messages: {
      loginRequired: 'Please log in to access your messages.',
      pageTitle: 'Messages',
      refreshConversations: 'Refresh conversations',
      searchPlaceholder: 'Search conversations…',
      unreadFilter: 'Unread only',
      emptyConversationsTitle: 'No conversations found',
      emptyConversationsBody: 'Start messaging from a listing or event to see conversations here.',
      noMessagesYet: 'No messages yet',
      conversationFallback: 'Conversation',
      generalConversation: 'General conversation',
      moreOptions: 'More options',
      view: 'View',
      writeMessage: 'Write a message…',
      sendMessage: 'Send message',
      pressEnterHint: 'Press Enter to send. Shift + Enter for a new line.',
      emptyThreadTitle: 'Select a conversation',
      emptyThreadBody: 'Choose a conversation from the list to view messages and reply.',
      reportUser: 'Report User',
      blockUser: 'Block User',
      loadConversationsError: 'Unable to load conversations. Please try again.',
      loadMessagesError: 'Unable to load messages. Please try again.',
      sendMessageError: 'Failed to send message. Please try again.',
      contextLabels: {
        listing: 'Listing',
        event: 'Event',
        conversation: 'Conversation',
      },
      relativeTime: {
        justNow: 'just now',
        minutesAgoSuffix: 'm ago',
        hoursAgoSuffix: 'h ago',
        daysAgoSuffix: 'd ago',
      },
    },
  },
  am: {
    header: {
      menuAriaLabel: 'ምናሌ',
      userFallback: 'ተጠቃሚ',
      subscription: 'ምዝገባ',
      pricing: 'ዋጋ',
      helpSupport: 'እርዳታ እና ድጋፍ',
      moreNavigation: 'ተጨማሪ አሰሳ',
    },
    footer: {
      description: 'የኢትዮጵያውያን እና የኤርትራውያን ማህበረሰቦችን በዓለም አቀፍ ደረጃ እናገናኛለን።',
      explore: 'ያስሱ',
      create: 'ይፍጠሩ',
      support: 'ድጋፍ',
      company: 'ኩባንያ',
      stayConnected: 'ተገናኝተው ይቆዩ',
      emailPlaceholder: 'ኢሜይልዎን ያስገቡ',
      subscribe: 'ይመዝገቡ',
      emailSupport: 'የኢሜይል ድጋፍ',
      phoneSupport: 'የስልክ ድጋፍ',
      globalReach: 'ዓለም አቀፍ ተደራሽነት',
      globalReachValue: 'በዓለም ዙሪያ 100+ ከተሞችን እናገለግላለን',
      privacy: 'ግላዊነት',
      terms: 'ውሎች',
      cookies: 'ኩኪዎች',
      accessibility: 'ተደራሽነት',
      rightsReserved: 'መብቶቹ ሁሉ የተጠበቁ ናቸው።',
      exploreLinks: {
        discover: 'ያስሱ',
        events: 'ዝግጅቶች',
        community: 'ማህበረሰብ',
      },
      createLinks: {
        listing: 'ዝርዝር',
        event: 'ዝግጅት',
      },
      supportLinks: {
        helpCenter: 'የእርዳታ ማዕከል',
        safety: 'ደህንነት',
        guidelines: 'መመሪያዎች',
      },
      companyLinks: {
        about: 'ስለ እኛ',
        careers: 'ስራዎች',
        blog: 'ብሎግ',
      },
    },
    home: {
      badge: 'ኢትዮጵያ። ኤርትራ። አንድ ህዝብ። በዓለም ዙሪያ።',
      heroTitleStart: 'ከማህበረሰብዎ ጋር ይተሳሰሩ',
      heroTitleHighlight: 'በዓለም ዙሪያ',
      heroSubtitleStart: 'ንግዶችን፣ መኖሪያን፣ ስራዎችን ያግኙ እና ጠንካራ ግንኙነቶችን ይገንቡ በ',
      heroSubtitleEmphasis: '200+ ከተሞች',
      heroSubtitleEnd: 'ዓለም አቀፍ ደረጃ።',
      primaryCta: 'አሁን ይጀምሩ',
      secondaryCta: 'ምርቶችን ያስሱ',
      socialProof: 'በዓለም ዙሪያ በሺዎች የሚታመን',
      productsEyebrow: 'አገልግሎቶች',
      productsTitleStart: 'ሙሉ በሙሉ የተዋሃደ የ',
      productsTitleHighlight: 'ማህበረሰብ አገልግሎቶች ስብስብ',
      productsSubtitle: 'የኢትዮጵያውያን እና የኤርትራውያን ማህበረሰቦችን በዓለም ዙሪያ ያገናኙ።',
      products: [
        {
          title: 'ዝርዝሮችን ይመልከቱ',
          description: 'በዓለም ዙሪያ ካሉ የኢትዮጵያውያን እና የኤርትራውያን ማህበረሰቦች የንግድ፣ የአገልግሎት እና የምርት ዝርዝሮችን ያግኙ።',
        },
        {
          title: 'ንብረት እና መኖሪያ',
          description: 'ለኪራይ፣ ለሽያጭ እና ለአብሮ መኖር የቀረቡ ዝርዝሮችን በመጠቀም ተስማሚ መኖሪያዎን ያግኙ።',
        },
        {
          title: 'ገበያ',
          description: 'በኢትዮጵያውያን እና በኤርትራውያን ማህበረሰብ ገበያ ውስጥ እቃዎችን፣ ጥበብ ስራዎችን እና ምርቶችን ይግዙ እና ይሽጡ።',
        },
        {
          title: 'ስራዎች እና እድሎች',
          description: 'በኢትዮጵያውያን እና በኤርትራውያን የንግድ አውታረ መረብ ውስጥ የስራ እድሎችን እና የሙያ እድገቶችን ያግኙ።',
        },
      ],
      explore: 'ያስሱ',
      whyEyebrow: 'ለምን መንግር',
      whyTitleStart: 'ለ',
      whyTitleHighlight: 'ማህበረሰብዎ የተገነባ',
      features: [
        {
          title: 'የባህል ትኩረት',
          description: 'ባህላዊ ግንዛቤን በመሠረት በማድረግ በተለይ ለኢትዮጵያውያን እና ለኤርትራውያን ማህበረሰቦች የተሰራ።',
        },
        {
          title: 'ዓለም አቀፍ አውታረ መረብ',
          description: 'በዓለም ዙሪያ በ200+ ከተሞች ያሉ የማህበረሰብ አባላት ጋር ይገናኙ።',
        },
        {
          title: 'የሚታመን መድረክ',
          description: 'ሊያምኗቸው የሚችሉ የተረጋገጡ ንግዶች እና የታመኑ የማህበረሰብ አባላት።',
        },
        {
          title: 'በማደግ ላይ ያለ ማህበረሰብ',
          description: 'በንቃት የሚሳተፉ እና እርስ በእርሳቸው የሚደጋገፉ በሺዎች አባላት ይቀላቀሉ።',
        },
      ],
      finalTitleStart: 'ለመጀመር',
      finalTitleHighlight: 'ዝግጁ ነዎት?',
      finalSubtitle: 'በዓለም ዙሪያ የሚገናኙ እና የሚያድጉ በሺዎች ወገኖችዎን ይቀላቀሉ።',
      finalPrimaryCta: 'አሁን ይጀምሩ',
      finalSecondaryCta: 'ዝርዝሮችን ይመልከቱ',
      stats: [
        { value: '200+', label: 'ከተሞች' },
        { value: '2K+', label: 'አባላት' },
        { value: '500+', label: 'ንግዶች' },
      ],
      footerDescription: 'የኢትዮጵያውያን እና የኤርትራውያን ማህበረሰቦችን በዓለም ዙሪያ እናገናኛለን',
    },
    login: {
      backToHome: 'ወደ መነሻ ተመለስ',
      joinTitle: 'መንግርን ይቀላቀሉ',
      welcomeTitle: 'ወደ መንግር እንኳን በደህና መጡ',
      joinSubtitle: 'መለያዎን በጥቂት ደቂቃዎች ውስጥ ይፍጠሩ።',
      welcomeSubtitle: 'ከኢትዮጵያውያን እና ከኤርትራውያን ማህበረሰብ ጋር ይገናኙ',
      continueWithGoogle: 'በGoogle ይቀጥሉ',
      continueWithEmail: 'በኢሜይል ይቀጥሉ',
      alreadyHaveAccount: 'መለያ አለዎት?',
      newToMengir: 'መንግር አዲስ ነዎት?',
      signIn: 'ግባ',
      createAccount: 'መለያ ፍጠር',
      secure: 'ደህንነቱ የተጠበቀ',
      instantAccess: 'ፈጣን መዳረሻ',
      termsIntro: 'በመቀጠልዎ የሚከተሉትን ይቀበላሉ',
      termsOfService: 'የአገልግሎት ውሎች',
      privacyPolicy: 'የግላዊነት ፖሊሲ',
      loading: 'በመጫን ላይ...',
      errors: {
        sessionExpired: 'የእርስዎ ክፍለ ጊዜ አልቋል። እባክዎ እንደገና ይግቡ።',
        configuration: 'የአገልጋይ ቅንብር ችግር አለ።',
        accessDenied: 'መዳረሻ ተከልክሏል። ፍቃድ ላይኖርዎት ይችላል።',
        verification: 'የማረጋገጫ ቶከኑ አልቋል ወይም አስቀድሞ ተጠቅመዋል።',
        oauthSignin: 'የፈቃድ URL ማቅረብ አልተሳካም።',
        oauthCallback: 'ማረጋገጫው አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
        oauthCreateAccount: 'መለያ መፍጠር አልተቻለም።',
        callback: 'ማረጋገጫው አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
        oauthAccountNotLinked: 'ኢሜይሉ ከሌላ መለያ ጋር ተያይዟል።',
        sessionRequired: 'ይህን ይዘት ለመድረስ መግባት ያስፈልጋል።',
        default: 'የማረጋገጫ ስህተት ተፈጥሯል። እባክዎ እንደገና ይሞክሩ።',
      },
    },
    community: {
      pageTitle: 'የማህበረሰብ ማስታወቂያ ሰሌዳ',
      pageSubtitle: 'ማስታወቂያዎችን ያጋሩ፣ ጥያቄዎችን ይጠይቁ እና የማህበረሰብ ስኬቶችን ያሳዩ።',
      newPost: 'አዲስ ልጥፍ',
      searchPlaceholder: 'የማህበረሰብ ልጥፎችን ይፈልጉ',
      allPostTypes: 'ሁሉም የልጥፍ አይነቶች',
      emptyState: 'እስካሁን ምንም የማህበረሰብ ልጥፎች የሉም። የመጀመሪያው ሰው ይሁኑ!',
      fallbackTitle: 'የማህበረሰብ ዝመና',
      linkedListing: 'ዝርዝር',
      linkedEvent: 'ዝግጅት',
      linkedUserPrefix: 'ተጠቃሚ',
      postTypes: {
        announcement: 'ማስታወቂያ',
        question: 'ጥያቄ',
        recommendation: 'ምክር',
        story: 'የስኬት ታሪክ',
        discussion: 'ውይይት',
      },
    },
    requests: {
      pageTitle: 'ጥያቄዎች እና የሚፈለጉ ነገሮች',
      pageSubtitle: 'የሚፈልጉትን ያጋሩ እና ከማህበረሰቡ እገዛ ሰጪዎች ጋር ይገናኙ።',
      newRequest: 'አዲስ ጥያቄ',
      searchPlaceholder: 'ጥያቄዎችን ይፈልጉ',
      allStatuses: 'ሁሉም ሁኔታዎች',
      emptyState: 'እስካሁን ምንም ጥያቄዎች የሉም። በአዲስ ጥያቄ ውይይቱን ይጀምሩ!',
      fallbackTitle: 'የማህበረሰብ ጥያቄ',
      statuses: {
        open: 'ክፍት',
        matched: 'ተጣመረ',
        closed: 'ዝግ',
        archived: 'ተቀምጧል',
      },
    },
    help: {
      pageTitle: 'እንዴት ልንረዳዎት እንችላለን?',
      pageSubtitle: 'መልሶችን ይፈልጉ፣ ድጋፍ ያግኙ ወይም አስተያየትዎን ያጋሩ',
      searchPlaceholder: 'የእርዳታ ጽሁፎችን ይፈልጉ...',
      quickFeedback: 'ፈጣን አስተያየት',
      quickFeedbackDescription: 'ልምድዎን ይገምግሙ እና ጥቆማዎችን ያጋሩ',
      getSupport: 'ድጋፍ ያግኙ',
      getSupportDescription: 'ችግር ያቅርቡ ወይም እርዳታ ይጠይቁ',
      tabs: {
        help: 'እርዳታ',
        newTicket: 'አዲስ ቲኬት',
        myTickets: 'የእኔ ቲኬቶች',
      },
      popularTopics: 'ተወዳጅ ርዕሶች',
      faqTitle: 'በብዛት የሚጠየቁ ጥያቄዎች',
      topics: [
        { id: 'listings', title: 'ዝርዝሮችን መፍጠር እና ማስተዳደር', articlesLabel: '5 ጽሁፎች' },
        { id: 'account', title: 'የመለያ ቅንብሮች', articlesLabel: '3 ጽሁፎች' },
        { id: 'payments', title: 'ክፍያዎች እና ሂሳብ', articlesLabel: '4 ጽሁፎች' },
        { id: 'safety', title: 'ደህንነት እና እምነት', articlesLabel: '6 ጽሁፎች' },
      ],
      faqs: [
        { q: 'ዝርዝር እንዴት እፈጥራለሁ?', a: 'የ"ዝርዝር ፍጠር" አዝራሩን ጠቅ ያድርጉ እና የደረጃ መመሪያውን ይከተሉ።' },
        { q: 'ሻጭን እንዴት አገናኛለሁ?', a: 'በማንኛውም ዝርዝር ላይ ያለውን "መልዕክት" አዝራር ተጠቅመው ውይይት ይጀምሩ።' },
        { q: 'ምዝገባዬን እንዴት አሻሽላለሁ?', a: 'ምዝገባዎን ለማየት እና ለማስተዳደር ወደ መለያ > ምዝገባ ይሂዱ።' },
        { q: 'ችግር እንዴት እሪፖርት አደርጋለሁ?', a: 'ከታች ያለውን "ድጋፍ ያግኙ" አማራጭ ተጠቅመው የድጋፍ ቲኬት ይፍጠሩ።' },
      ],
    },
    pricing: {
      pageTitle: 'እቅድዎን ይምረጡ',
      pageSubtitle: 'ለማህበረሰብዎ AI የተጎላበተ ይዘት ፍጠር እና ፕሪሚየም ባህሪያትን ይክፈቱ',
      checkoutCancelledTitle: 'የክፍያ ሂደቱ ተሰርዟል',
      checkoutCancelledBody: 'ችግር የለም! በማንኛውም ጊዜ መመዝገብ ይችላሉ። ከታች እቅድ ይምረጡ።',
      manage: 'አስተዳድር',
      activeSubscription: 'ንቁ ምዝገባ አለዎት',
      freePlan: 'ነፃ',
      freePlanDescription: 'በመሠረታዊ ባህሪያት ይጀምሩ',
      forever: 'ለዘላለም',
      currentPlan: 'የአሁኑ እቅድ',
      freeFeatures: [
        '5 AI የተጎላበቱ ዝርዝሮች/ዝግጅቶች',
        'መሠረታዊ የማህበረሰብ መዳረሻ',
        'መደበኛ የፍለጋ ታይነት',
        'የኢሜይል ድጋፍ',
      ],
      paidFeatures: [
        'ቅድሚያ ያለው AI ይዘት ፍጠር',
        'የተሻለ የፍለጋ ታይነት',
        'በዝርዝሮች ላይ ፕሪሚየም ምልክት',
        'የላቀ ትንታኔ',
        'ቅድሚያ ያለው የደንበኛ ድጋፍ',
        'ለአዳዲስ ባህሪያት ቀዳሚ መዳረሻ',
      ],
      popular: 'ተወዳጅ',
      getStarted: 'ይጀምሩ',
      needHelpChoosing: 'ለመምረጥ እርዳታ ይፈልጋሉ?',
      allPlansInclude: 'ሁሉም እቅዶች ያልተገደበ አሰሳ እና ግንኙነት ያካትታሉ',
      viewMySubscription: 'ምዝገባዬን ይመልከቱ',
      errors: {
        checkoutSession: 'የክፍያ ክፍለ-ጊዜ መፍጠር አልተቻለም',
        subscriptionStart: 'ምዝገባውን ማስጀመር አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
        billingPortal: 'የሂሳብ መግቢያ መክፈት አልተቻለም',
        upgradePortal: 'የማሻሻያ መግቢያ መክፈት አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
      },
      interval: {
        singlePrefix: '/ ',
        multiPrefix: '/ ',
        pluralSuffix: '',
      },
      aiGenerationsPerMonth: 'AI ፍጠራዎች በወር',
    },
    messages: {
      loginRequired: 'መልዕክቶችዎን ለማግኘት እባክዎ ይግቡ።',
      pageTitle: 'መልዕክቶች',
      refreshConversations: 'ውይይቶችን አድስ',
      searchPlaceholder: 'ውይይቶችን ይፈልጉ…',
      unreadFilter: 'ያልተነበቡ ብቻ',
      emptyConversationsTitle: 'ምንም ውይይት አልተገኘም',
      emptyConversationsBody: 'ውይይቶችን እዚህ ለማየት ከዝርዝር ወይም ከዝግጅት ውስጥ መልዕክት ጀምሩ።',
      noMessagesYet: 'እስካሁን ምንም መልዕክቶች የሉም',
      conversationFallback: 'ውይይት',
      generalConversation: 'አጠቃላይ ውይይት',
      moreOptions: 'ተጨማሪ አማራጮች',
      view: 'ተመልከት',
      writeMessage: 'መልዕክት ይጻፉ…',
      sendMessage: 'መልዕክት ላክ',
      pressEnterHint: 'ለመላክ Enter ይጫኑ። ለአዲስ መስመር Shift + Enter ይጠቀሙ።',
      emptyThreadTitle: 'ውይይት ይምረጡ',
      emptyThreadBody: 'መልዕክቶችን ለማየት እና ለመመለስ ከዝርዝሩ ውስጥ ውይይት ይምረጡ።',
      reportUser: 'ተጠቃሚውን ሪፖርት አድርግ',
      blockUser: 'ተጠቃሚውን አግድ',
      loadConversationsError: 'ውይይቶችን መጫን አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
      loadMessagesError: 'መልዕክቶችን መጫን አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
      sendMessageError: 'መልዕክት መላክ አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
      contextLabels: {
        listing: 'ዝርዝር',
        event: 'ዝግጅት',
        conversation: 'ውይይት',
      },
      relativeTime: {
        justNow: 'አሁን ብቻ',
        minutesAgoSuffix: 'ደቂቃ በፊት',
        hoursAgoSuffix: 'ሰዓት በፊት',
        daysAgoSuffix: 'ቀን በፊት',
      },
    },
  },
}

export function getUiCopy(locale: string): UiCopy {
  return locale === 'am' ? uiCopy.am : uiCopy.en
}
